import type { ForwardedRef, ReactElement, RefAttributes } from 'react';
import { forwardRef, useMemo } from 'react';
import {
  type GroupBase,
  type MultiValueRemoveProps,
  components as reactSelectComponents,
  type SelectComponentsConfig,
  type SelectInstance,
} from 'react-select';

import { Select, type SelectProps } from '@revelio/ui';

import './entity-search-select.module.css';

type EntitySearchSelectProps<TOption> = Omit<
  SelectProps<TOption, true, GroupBase<TOption>>,
  'isMulti'
> & {
  /**
   * When set (e.g. `1`), the chip remove "x" will be hidden when the number of
   * selected values is <= this number.
   *
   * This is useful for defaults that must never be empty.
   */
  minSelectedValues?: number;
};

const baseComponents = {
  DropdownIndicator: () => null,
  IndicatorSeparator: () => null,
} as const;

const createMinSelectedValuesRemove = <TOption,>({
  minSelectedValues,
  baseRemoveComponent,
}: {
  minSelectedValues: number;
  baseRemoveComponent:
    | SelectComponentsConfig<
        TOption,
        true,
        GroupBase<TOption>
      >['MultiValueRemove']
    | undefined;
}) => {
  const RemoveComponent =
    baseRemoveComponent ?? reactSelectComponents.MultiValueRemove;

  return (props: MultiValueRemoveProps<TOption, true, GroupBase<TOption>>) => {
    const selectedOptions = props.selectProps.value as TOption[];
    if (selectedOptions.length <= minSelectedValues) {
      return null;
    }

    return <RemoveComponent {...props} />;
  };
};

type EntitySearchSelectComponent = <TOption = unknown>(
  props: EntitySearchSelectProps<TOption> &
    RefAttributes<SelectInstance<TOption, true, GroupBase<TOption>>>
) => ReactElement;

export const EntitySearchSelect: EntitySearchSelectComponent = forwardRef(
  function EntitySearchSelectInner<TOption>(
    {
      minSelectedValues,
      components,
      closeMenuOnSelect = false,
      isClearable = false,
      isSearchable = true,
      maxMenuHeight = 200,
      ...props
    }: EntitySearchSelectProps<TOption>,
    ref: ForwardedRef<SelectInstance<TOption, true, GroupBase<TOption>>>
  ) {
    const mergedComponents = useMemo(() => {
      const minRemove =
        typeof minSelectedValues === 'number' && minSelectedValues > 0
          ? createMinSelectedValuesRemove<TOption>({
              minSelectedValues,
              baseRemoveComponent: components?.MultiValueRemove,
            })
          : undefined;

      return {
        ...baseComponents,
        ...(components ?? {}),
        ...(minRemove ? { MultiValueRemove: minRemove } : {}),
      };
    }, [components, minSelectedValues]);

    return (
      <Select<TOption, true, GroupBase<TOption>>
        classNamePrefix="entity-search-select"
        {...props}
        ref={ref}
        isMulti
        closeMenuOnSelect={closeMenuOnSelect}
        isClearable={isClearable}
        isSearchable={isSearchable}
        maxMenuHeight={maxMenuHeight}
        components={mergedComponents}
      />
    );
  }
) as unknown as EntitySearchSelectComponent;
